// Season editor (New Season): pick a season (Pre season = template, Active,
// Archive), edit it, "Save changes" -> PUT v2/seasons/... (one PUT per season in
// bulk edit, writing only the fields the user changed).
const React = require('react');
const { useState, useEffect, useMemo, useCallback, useRef } = require('react');
const { useDispatch, useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const {
  save_new_season_season,
  load_new_season_season_detail,
} = require('@actions/newSeasonActions');
const {
  findV2Distribution,
  findV2FarmingUnit,
  sortByName,
} = require('./v2Distributions');
const helpers = require('./seasonHelpers');
const { getBlockAreas } = require('./layoutHelpers');
const { buildSeasonSaveBody } = require('./seasonSaveBody');
const {
  farmingUnitEditorUrl,
  seasonEditorUrl,
} = require('./newSeasonConstants');
const { useToast, Toast, SegmentedTabs } = require('./NewSeasonUi');
const BlocksMap = require('./BlocksMap');
const SeasonList = require('./SeasonList');
const SensorsSection = require('./SensorsSection');
const {
  Section,
  GeneralSection,
  DatesSection,
  IrrigationSection,
  ContractSection,
  AdvanceSection,
} = require('./SeasonForm');

const { SEASON_TABS } = helpers;

// Distribution / farming-unit selects come from GET v2/distributions only.
const useDistributions = () => {
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  return useMemo(() => sortByName(distributions), [distributions]);
};

const EntitySelects = ({
  distributions,
  distributionId,
  farmingUnitId,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const distribution = findV2Distribution(distributions, distributionId);
  const farmingUnits = sortByName(distribution?.farming_units);
  const onDistribution = (id) => {
    const next = findV2Distribution(distributions, id);
    const firstFarmingUnit = sortByName(next?.farming_units)[0];
    if (firstFarmingUnit) {
      onNavigate(seasonEditorUrl(id, firstFarmingUnit.id));
    }
  };
  return (
    <div className='ns-entity-selects'>
      <select
        className='form-control custom-select ns-input'
        value={distributionId}
        aria-label={t('distribution')}
        onChange={(e) => onDistribution(e.target.value)}
      >
        {!distribution && (
          <option value={distributionId}>{distributionId}</option>
        )}
        {distributions.map((current) => (
          <option key={current.id} value={current.id}>
            {current.name}
          </option>
        ))}
      </select>
      <select
        className='form-control custom-select ns-input'
        value={farmingUnitId}
        aria-label={t('farming_unit')}
        onChange={(e) =>
          onNavigate(seasonEditorUrl(distributionId, e.target.value))
        }
      >
        {!farmingUnits.some((current) => current.id === farmingUnitId) && (
          <option value={farmingUnitId}>{farmingUnitId}</option>
        )}
        {farmingUnits.map((current) => (
          <option key={current.id} value={current.id}>
            {current.name}
          </option>
        ))}
      </select>
    </div>
  );
};

// Selection: a single selected row, or the checked rows in bulk mode.
const useSeasonSelection = (rows, tab) => {
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [checkedKeys, setCheckedKeys] = useState([]);
  useEffect(() => {
    setSelectedKey(null);
    setCheckedKeys([]);
  }, [tab, bulkMode]);
  const rowByKey = useMemo(
    () => new Map(rows.map((row) => [row.key, row])),
    [rows],
  );
  const effectiveKey = rowByKey.has(selectedKey) ? selectedKey : rows[0]?.key;
  const activeRows = bulkMode
    ? checkedKeys.map((key) => rowByKey.get(key)).filter(Boolean)
    : [rowByKey.get(effectiveKey)].filter(Boolean);
  const toggle = useCallback(
    (key) =>
      setCheckedKeys((previous) =>
        previous.includes(key)
          ? previous.filter((current) => current !== key)
          : previous.concat([key]),
      ),
    [],
  );
  const toggleAll = () =>
    setCheckedKeys((previous) =>
      previous.length === rows.length ? [] : rows.map((row) => row.key),
    );
  return {
    bulkMode,
    setBulkMode,
    selectedKey: effectiveKey,
    setSelectedKey,
    checkedKeys,
    toggle,
    toggleAll,
    activeRows,
  };
};

// Same array instance while the active seasons are the same objects, so the
// form only resets when the selection changes or a season is re-saved.
const useStableSeasons = (activeRows) => {
  const previousRef = useRef([]);
  const seasons = activeRows.map((row) => row.season);
  const previous = previousRef.current;
  const isSame =
    previous.length === seasons.length &&
    seasons.every((season, index) => season === previous[index]);
  if (!isSame) {
    previousRef.current = seasons;
  }
  return previousRef.current;
};

// Form values for the active seasons; reset whenever the active seasons change.
const useSeasonForm = (activeRows, setDirty) => {
  const seasons = useStableSeasons(activeRows);
  const initialValues = useMemo(() => helpers.commonValues(seasons), [seasons]);
  const [values, setValues] = useState(initialValues);
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);
  const changed = helpers.changedKeys(initialValues, values);
  useEffect(() => {
    setDirty(changed.length > 0);
  }, [changed.length, setDirty]);
  const onChange = useCallback(
    (key, value) => setValues((previous) => ({ ...previous, [key]: value })),
    [],
  );
  const onChangeMany = useCallback(
    (patch) => setValues((previous) => ({ ...previous, ...patch })),
    [],
  );
  return { values, changed, onChange, onChangeMany };
};

// GET v2/seasons/... for every active season: {[seasonId]: {season} | {error}}.
// The detail is the only source of a season's sensors (the tree has none).
const useSeasonDetails = (distributionId, farmingUnitId, activeRows) => {
  const dispatch = useDispatch();
  const [details, setDetails] = useState({});
  const seasonIds = activeRows.map((row) => row.season.id);
  const missingKey = seasonIds.filter((id) => !details[id]).join('|');
  const fetchDetail = useCallback(
    (seasonId) =>
      dispatch(
        load_new_season_season_detail(distributionId, farmingUnitId, seasonId),
      ).then((detail) => detail.season),
    [dispatch, distributionId, farmingUnitId],
  );
  const setDetail = useCallback(
    (seasonId, entry) =>
      setDetails((previous) => ({ ...previous, [seasonId]: entry })),
    [],
  );
  useEffect(() => {
    missingKey
      .split('|')
      .filter(Boolean)
      .forEach((seasonId) =>
        fetchDetail(seasonId).then(
          (season) => setDetail(seasonId, { season }),
          (error) => setDetail(seasonId, { error: String(error) }),
        ),
      );
  }, [missingKey, fetchDetail, setDetail]);
  return { details, fetchDetail, setDetail };
};

const saveSequentially = (rows, save) =>
  rows.reduce(
    (chain, row) =>
      chain.then((result) =>
        Promise.resolve(save(row))
          .then(() => ({ ...result, saved: result.saved + 1 }))
          .catch((error) => ({
            ...result,
            failed: result.failed.concat([row.block.name + ': ' + error]),
          })),
      ),
    Promise.resolve({ saved: 0, failed: [] }),
  );

const useSaveSeasons = ({
  distributionId,
  farmingUnitId,
  activeRows,
  form,
  seasonDetails,
  showToast,
  t,
}) => {
  const dispatch = useDispatch();
  const distributions = useSelector(
    (state) => state.new_season_data?.distributions,
  );
  const farmingUnit = findV2FarmingUnit(
    distributions,
    distributionId,
    farmingUnitId,
  );
  const [isSaving, setIsSaving] = useState(false);
  // Fresh detail right before each PUT, so each season sends its own,
  // current sensors; the PUT response (with sensors) refreshes the detail.
  const saveRow = (row, changes) =>
    seasonDetails.fetchDetail(row.season.id).then((detailSeason) => {
      let body;
      try {
        body = buildSeasonSaveBody(detailSeason, changes, farmingUnit);
      } catch (error) {
        return Promise.reject(t('new_season_sensors_missing'));
      }
      return Promise.resolve(
        dispatch(
          save_new_season_season(
            distributionId,
            farmingUnitId,
            row.season.id,
            body,
          ),
        ),
      ).then((saved) => {
        if (saved) {
          seasonDetails.setDetail(row.season.id, { season: saved });
        }
      });
    });
  const save = () => {
    if (form.changed.length === 0) {
      showToast(t('new_season_nothing_changed'), 'error');
      return;
    }
    const changes = helpers.pick(form.values, form.changed);
    setIsSaving(true);
    saveSequentially(activeRows, (row) => saveRow(row, changes)).then(
      ({ saved, failed }) => {
        setIsSaving(false);
        if (failed.length === 0) {
          showToast(
            activeRows.length > 1
              ? t('new_season_bulk_saved', { count: saved })
              : t('new_season_changes_saved'),
          );
        } else {
          showToast(
            t('new_season_bulk_partial', {
              saved,
              total: activeRows.length,
              failed: failed.join('; '),
            }),
            'error',
          );
        }
      },
    );
  };
  return { isSaving, save };
};

const useMapBlocks = (tree) =>
  useMemo(
    () =>
      (tree.field_groups || []).reduce(
        (blocks, fieldGroup) =>
          blocks.concat(
            (fieldGroup.blocks || []).map((block) => ({
              key: block.id,
              name: block.name,
              areas: getBlockAreas(block),
            })),
          ),
        [],
      ),
    [tree],
  );

const SeasonFormPanel = ({
  tab,
  activeRows,
  form,
  mapBlocks,
  onMapBlockClick,
  isSaving,
  onSave,
  seasonDetails,
  fitKey,
}) => {
  const { t } = useTranslation();
  const selectedBlockKeys = useMemo(
    () => activeRows.map((row) => row.block.id),
    [activeRows],
  );
  if (activeRows.length === 0) {
    return (
      <div className='ns-card ns-right-card ns-center-message'>
        {t('new_season_select_season')}
      </div>
    );
  }
  const isTemplate = activeRows.every((row) => row.season.state === 'template');
  return (
    <div className='ns-card ns-right-card ns-season-form'>
      <div className='ns-title-bar ns-title-static'>
        {helpers.seasonTitle(activeRows[0], tab)}
      </div>
      <div className='ns-form-scroll'>
        <Section title={t('new_season_section_map')}>
          <BlocksMap
            className='ns-map-medium'
            blocks={mapBlocks}
            selectedKeys={selectedBlockKeys}
            focusKeys={selectedBlockKeys}
            fitKey={fitKey}
            onBlockClick={onMapBlockClick}
          />
        </Section>
        <GeneralSection
          values={form.values}
          onChange={form.onChange}
          onChangeMany={form.onChangeMany}
        />
        <DatesSection values={form.values} onChange={form.onChange} />
        <IrrigationSection values={form.values} onChange={form.onChange} />
        {!isTemplate && (
          <SensorsSection
            rows={activeRows}
            details={seasonDetails.details}
            mapBlocks={mapBlocks}
            selectedBlockKeys={selectedBlockKeys}
          />
        )}
        <ContractSection values={form.values} onChange={form.onChange} />
        <AdvanceSection />
      </div>
      <div className='ns-sticky-footer'>
        <button
          type='button'
          className='ns-btn ns-btn-primary'
          disabled={isSaving}
          onClick={onSave}
        >
          {t('new_season_save_changes')}
          {isSaving && (
            <span
              className='spinner-border spinner-border-sm ml-2'
              role='status'
            />
          )}
        </button>
      </div>
    </div>
  );
};

const SeasonEditor = ({
  distributionId,
  farmingUnitId,
  tree,
  setDirty,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const distributions = useDistributions();
  const [tab, setTab] = useState(SEASON_TABS.ACTIVE);
  const groups = useMemo(
    () => helpers.buildSeasonGroups(tree, tab),
    [tree, tab],
  );
  const rows = useMemo(() => helpers.flattenRows(groups), [groups]);
  const selection = useSeasonSelection(rows, tab);
  const form = useSeasonForm(selection.activeRows, setDirty);
  const { toast, showToast } = useToast();
  const seasonDetails = useSeasonDetails(
    distributionId,
    farmingUnitId,
    selection.activeRows,
  );
  const { isSaving, save } = useSaveSeasons({
    distributionId,
    farmingUnitId,
    activeRows: selection.activeRows,
    form,
    seasonDetails,
    showToast,
    t,
  });
  const mapBlocks = useMapBlocks(tree);

  const onMapBlockClick = (blockId) => {
    const row = rows.find((current) => current.block.id === blockId);
    if (row) {
      selection.bulkMode
        ? selection.toggle(row.key)
        : selection.setSelectedKey(row.key);
    }
  };
  const tabs = [
    { value: SEASON_TABS.PRE_SEASON, label: t('new_season_pre_season') },
    { value: SEASON_TABS.ACTIVE, label: t('new_season_active') },
    { value: SEASON_TABS.ARCHIVE, label: t('new_season_archive') },
  ];
  return (
    <div className='ns-editor'>
      <div className='ns-card ns-left-card'>
        <div className='ns-card-title'>{t('new_season_manage_seasons')}</div>
        <EntitySelects
          distributions={distributions}
          distributionId={distributionId}
          farmingUnitId={farmingUnitId}
          onNavigate={onNavigate}
        />
        <SegmentedTabs options={tabs} value={tab} onChange={setTab} />
        <label className='ns-toggle'>
          <input
            type='checkbox'
            checked={selection.bulkMode}
            onChange={() => selection.setBulkMode((on) => !on)}
          />
          <span>
            {selection.bulkMode
              ? t('new_season_bulk_edit')
              : t('new_season_bulk_select')}
          </span>
        </label>
        <div className='ns-list-scroll'>
          <SeasonList
            tab={tab}
            groups={groups}
            selectedKey={selection.selectedKey}
            bulkMode={selection.bulkMode}
            checkedKeys={selection.checkedKeys}
            onSelect={selection.setSelectedKey}
            onToggle={selection.toggle}
            onToggleAll={selection.toggleAll}
          />
        </div>
        {tab === SEASON_TABS.ARCHIVE && (
          <button
            type='button'
            className='ns-btn ns-btn-outline ns-btn-block'
            onClick={() =>
              onNavigate(farmingUnitEditorUrl(distributionId, farmingUnitId))
            }
          >
            {t('new_season_edit_fields_and_blocks')}
          </button>
        )}
      </div>
      <div className='ns-right-column'>
        <SeasonFormPanel
          tab={tab}
          activeRows={selection.activeRows}
          form={form}
          mapBlocks={mapBlocks}
          onMapBlockClick={onMapBlockClick}
          isSaving={isSaving}
          onSave={save}
          seasonDetails={seasonDetails}
          fitKey={distributionId + '/' + farmingUnitId}
        />
      </div>
      <Toast toast={toast} />
    </div>
  );
};

module.exports = SeasonEditor;
